// eslint-disable-next-line import/no-unresolved
import KeenSlider from 'https://cdn.skypack.dev/pin/keen-slider@v6.6.14-XM80lRoYRUdkHMpUfMaX/mode=imports,min/optimized/keen-slider.js'

const { debounce } = window.BAO.Utils
const { config } = window.BAO.Carousel
const {
  setInitialised,
  useArrows,
  addWheelControls,
} = window.BAO.Carousel.plugins
const productCardHtmlCache = new Map()
const productCardHtmlInFlight = new Set()

class ProductSwatchGroup extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['items', 'item']
  }

  static get requiredExternalElements () {
    return ['next']
  }

  connectedCallback () {
    super.connectedCallback()
    this.setupPrefetchObserver()
    if (this.els?.item?.elements?.length > 1) {
      this.carousel = this.setupCarousel()
    }
  }

  setupCarousel () {
    const initialSlide = this.dataset.initialSlide
      ? parseInt(this.dataset.initialSlide, 10)
      : 0

    if (this.dataset.initialSlide) {
      delete this.dataset.initialSlide
    }

    return new KeenSlider(
      this,
      {
        selector: this.els.item.selector,
        loop: false,
        mode: 'free',
        renderMode: 'performance',
        rubberband: false,
        initial: initialSlide,
        defaultAnimation: {
          duration: config.defaultAnimation.duration,
        },
        dragSpeed: config.dragSpeed,
        slides: {
          perView: 'auto',
          spacing: 12,
        },
      },
      [
        setInitialised(),
        useArrows({
          next: this.externalEls?.next?.element,
        }),
        addWheelControls('horizontal'),
        this.updateOverflowClasses(),
      ],
    )
  }

  updateOverflowClasses () {
    return carousel => {
      const updateClasses = () => {
        const { maxIdx, abs } = carousel.track.details

        const hasOverflow = maxIdx > 0
        this.classList.toggle('prd-ColourSwatch_Group-overflowing', hasOverflow)

        if (!hasOverflow) {
          this.classList.remove('prd-ColourSwatch_Group-atEnd')
          return
        }

        const atEnd = abs === maxIdx
        this.classList.toggle('prd-ColourSwatch_Group-atEnd', atEnd)
      }

      carousel.on('created', updateClasses)
      carousel.on('slideChanged', updateClasses)
      carousel.on('updated', updateClasses)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.handleMouseOver = this.handleMouseOver.bind(this)
    this.handleClick = this.handleClick.bind(this)

    this.debouncedHandleMouseOver = debounce(
      event => this.handleMouseOver(event),
      20,
    )

    this.els.item.elements.forEach(item => {
      this.listeners.add(item, 'mouseover', this.debouncedHandleMouseOver)
      this.listeners.add(item, 'click', this.handleClick)
    })
  }

  async handleClick (e) {
    const thisItem = e.target.closest(this.els.item.selector)
    if (!thisItem?.dataset?.handle) return

    const thisProductHandle = thisItem.dataset.handle

    const currentSlideIndex = this.carousel?.track?.details?.abs

    e.preventDefault()
    e.stopPropagation()

    await this.prefetchProductCardHtml(thisProductHandle)

    this.replaceProductCardHtml(thisProductHandle, currentSlideIndex)
  }

  async handleMouseOver (e) {
    const thisItem = e.target.closest(this.els.item.selector)
    if (!thisItem?.dataset?.handle) return

    const thisProductHandle = thisItem.dataset.handle
    if (this.hasProductCardHtml(thisProductHandle)) return

    await this.prefetchProductCardHtml(thisProductHandle)
  }

  async getProductCardHtml (productHandle) {
    const productUrl = `${Shopify.routes.root}products/${productHandle}`

    try {
      const html = await window.BAO.sectionRenderer.fetchSingle(
        'product-card',
        productUrl,
      )

      return new DOMParser().parseFromString(html, 'text/html')
    } catch (e) {
      console.error('Error fetching section:', e)

      return ''
    }
  }

  replaceProductCardHtml (productHandle, slideIndex) {
    const productCardHtml = productCardHtmlCache.get(productHandle)
    const currentProductCardEl = this.closest('[is="product-card"]')
    if (!currentProductCardEl) return

    const newProductCardEl = productCardHtml?.querySelector?.(
      '[is="product-card"]',
    )
    if (!newProductCardEl) return

    this.updateCurrentSwatchInCard(newProductCardEl, productHandle)

    const currentOverflowClasses = {
      overflowing: this.classList.contains(
        'prd-ColourSwatch_Group-overflowing',
      ),
      atEnd: this.classList.contains('prd-ColourSwatch_Group-atEnd'),
    }

    const newProductCardClone = newProductCardEl.cloneNode(true)
    const newCardSwatchGroup = newProductCardClone.querySelector(
      'product-swatch-group',
    )

    if (newCardSwatchGroup) {
      newCardSwatchGroup.classList.toggle(
        'prd-ColourSwatch_Group-overflowing',
        currentOverflowClasses.overflowing,
      )
      newCardSwatchGroup.classList.toggle(
        'prd-ColourSwatch_Group-atEnd',
        currentOverflowClasses.atEnd,
      )

      if (slideIndex != null) {
        newCardSwatchGroup.dataset.initialSlide = slideIndex
      }
    }

    currentProductCardEl.replaceWith(newProductCardClone)
  }

  hasProductCardHtml (productHandle) {
    return productCardHtmlCache.has(productHandle)
  }

  updateCurrentSwatchInCard (productCardEl, productHandle) {
    if (!productCardEl) return

    const swatchGroupEl = productCardEl.querySelector('product-swatch-group')
    const swatchKey = swatchGroupEl?.getAttribute('key')
    const swatchItemSelector = swatchKey
      ? `[data-product-swatch-group-${swatchKey}-el="item"]`
      : '[data-product-swatch-group-el="item"]'

    const swatchItems = productCardEl.querySelectorAll(swatchItemSelector)
    if (!swatchItems.length) return

    swatchItems.forEach(item => {
      const itemContainer = item.closest('.prd-ColourSwatch_Item')
      if (!itemContainer) return

      itemContainer.classList.toggle(
        'prd-ColourSwatch_Item-current',
        item.dataset.handle === productHandle,
      )
    })
  }

  setupPrefetchObserver () {
    const productCardEl = this.closest('[is="product-card"]')
    if (!productCardEl) return

    if (!('IntersectionObserver' in window)) {
      this.prefetchOnIdle()
      return
    }

    this.prefetchObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          this.prefetchOnIdle()
          this.prefetchObserver?.disconnect()
        })
      },
      { rootMargin: '0px 0px 800px 0px', threshold: 0 },
    )

    this.prefetchObserver.observe(productCardEl)
  }

  prefetchOnIdle () {
    const handles = this.getSwatchHandles()
    if (!handles.length) return

    const schedule = window.requestIdleCallback
      ? window.requestIdleCallback
      : cb => window.setTimeout(cb, 0)

    schedule(() => {
      handles.forEach(handle => this.prefetchProductCardHtml(handle))
    })
  }

  getSwatchHandles () {
    return this.els.item.elements
      .map(item => item.dataset?.handle)
      .filter(Boolean)
  }

  async prefetchProductCardHtml (productHandle) {
    if (
      this.hasProductCardHtml(productHandle) ||
      productCardHtmlInFlight.has(productHandle)
    ) {
      return
    }

    productCardHtmlInFlight.add(productHandle)
    const html = await this.getProductCardHtml(productHandle)
    if (html) productCardHtmlCache.set(productHandle, html)
    productCardHtmlInFlight.delete(productHandle)
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    this.prefetchObserver?.disconnect()
    this.carousel?.destroy()
  }
}

if (!customElements.get('product-swatch-group')) {
  customElements.define('product-swatch-group', ProductSwatchGroup)
}
